import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { StatusBadge, Overline } from "@/components/ui-bits";
import { toast } from "sonner";
import {
  Rocket, Search, Plus, ExternalLink, ShieldCheck, RotateCcw, History,
  X, Loader2, Globe, Terminal, CheckCircle2, XCircle, Server, Check, Ban, Lock, PackageCheck,
  Coins, MinusCircle, PlusCircle, AlertCircle,
} from "lucide-react";

export default function Deployments() {
  const { user } = useOutletContext() || {};
  const isAdmin = user?.role === "admin";
  const [deps, setDeps] = useState([]);
  const [config, setConfig] = useState({ vercel: false, registrar: false, root_domain: null });
  const [agents, setAgents] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/deployments").then((r) => setDeps(r.data)).catch(() => {});
  const loadAgents = () => api.get("/agents").then((r) => {
    setAllAgents(r.data);
    setAgents(r.data.filter((a) => a.role === "builder" && a.status === "active"));
  }).catch(() => {});
  useEffect(() => {
    load();
    api.get("/deploy/config").then((r) => setConfig(r.data)).catch(() => {});
    loadAgents();
    const i = setInterval(() => { load(); loadAgents(); }, 8000);
    return () => clearInterval(i);
  }, []);
  useRealtime(() => { load(); loadAgents(); });

  const budgetFor = (agentId) => {
    const a = allAgents.find((x) => x.id === agentId);
    if (!a || !a.daily_cap) return null;
    const today = new Date().toISOString().slice(0, 10);
    const spentToday = a.spent_today_date === today ? (a.spent_today || 0) : 0;
    const cap = a.daily_cap;
    const remaining = Math.max(0, cap - spentToday);
    return { remaining, cap, spentToday, pct: Math.min(100, (spentToday / cap) * 100), name: a.name };
  };

  const pollStatus = async (dep) => {
    try {
      await api.get(`/deployments/${dep.id}/status`);
      load();
    } catch { /* */ }
  };
  useEffect(() => {
    const building = deps.filter((d) => d.status === "deploying" || d.status === "generating");
    if (building.length) {
      const t = setTimeout(() => building.forEach((d) => (d.status === "deploying" ? pollStatus(d) : load())), 3000);
      return () => clearTimeout(t);
    }
  }, [deps]);

  const redeploy = async (dep) => {
    toast.loading("Regenerating & redeploying…", { id: dep.id });
    try {
      await api.post(`/deployments/${dep.id}/redeploy`);
      toast.success("Redeploy started — new version building", { id: dep.id });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Redeploy failed", { id: dep.id }); }
  };

  const retry = async (dep) => {
    toast.loading("Retrying deployment…", { id: dep.id });
    try {
      const r = await api.post(`/deployments/${dep.id}/retry`);
      const st = r.data?.status;
      toast.success(st === "awaiting_approval" ? "Retry queued — awaiting your deploy approval" : "Retry started — building now", { id: dep.id });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Retry failed", { id: dep.id }); }
  };

  const buildTask = async (dep) => {
    toast.loading("Handing to Builder — generating & shipping…", { id: dep.id });
    try {
      const r = await api.post(`/deployments/${dep.id}/build`);
      const st = r.data?.status;
      toast.success(st === "awaiting_approval" ? "Build queued — awaiting your deploy approval" : "Build started — shipping now", { id: dep.id });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Build failed", { id: dep.id }); }
  };


  const rollback = async (dep, version) => {
    toast.loading(`Rolling back to v${version}…`, { id: dep.id });
    try {
      await api.post(`/deployments/${dep.id}/rollback`, { version });
      toast.success(`Rolled back to v${version}`, { id: dep.id });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Rollback failed", { id: dep.id }); }
  };

  const approveDeploy = async (dep, approve) => {
    toast.loading(approve ? "Approving deploy…" : "Rejecting deploy…", { id: dep.id });
    try {
      await api.post(`/deployments/${dep.id}/approve`, { approve });
      toast[approve ? "success" : "error"](approve ? "Deploy approved — shipping to production" : "Deploy rejected — nothing shipped, no cost charged", { id: dep.id });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed", { id: dep.id }); }
  };

  const requestRevision = async (dep) => {
    const note = window.prompt(`What tweak should ${dep.agent_name} make to "${dep.product_name}"?`);
    if (!note || !note.trim()) return;
    toast.loading("Requesting revision…", { id: dep.id });
    try {
      await api.post(`/deployments/${dep.id}/revision`, { note: note.trim() });
      toast.success("Revision requested — builder will ship a new version", { id: dep.id });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Revision failed", { id: dep.id }); }
  };

  return (
    <div className="space-y-6" data-testid="deployments-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Overline>Domains · Build · Deploy · SSL</Overline>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Deployment Pipeline</h1>
        </div>
        <button data-testid="new-deployment-btn" onClick={() => setShowNew(true)} disabled={!config.vercel}
          className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 transition-colors disabled:opacity-50">
          <Rocket className="w-4 h-4" /> New Deployment
        </button>
      </div>

      <div className="flex flex-wrap gap-2 text-[11px] font-mono">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${config.vercel ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-red-500/30 bg-red-500/10 text-red-400"}`}>
          {config.vercel ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} Vercel deploy {config.vercel ? "connected" : "not configured"}
        </span>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${config.root_domain ? "border-cyan-500/30 bg-cyan-500/10 text-cyan-400" : "border-slate-700 bg-slate-800/50 text-slate-400"}`} data-testid="branded-domain-chip">
          {config.root_domain ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {config.root_domain ? `Branded domains · *.${config.root_domain}` : "No branded root domain"}
        </span>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${config.registrar ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-slate-700 bg-slate-800/50 text-slate-400"}`}>
          {config.registrar ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} Registrar purchase {config.registrar ? "connected" : "search-only (add GoDaddy PAT to enable purchase)"}
        </span>
      </div>

      <DomainSearch enabled={config.vercel} />

      {deps.length === 0 && (
        <div className="hud-card rounded-lg p-12 text-center">
          <Server className="w-10 h-10 text-slate-600 mx-auto" />
          <p className="mt-3 text-slate-400">No deployments yet. A builder agent can generate & ship a live site with SSL in one click.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {deps.map((d) => (
          <div key={d.id} className="hud-card accent-top rounded-lg p-5" style={{ "--accent-line": d.status === "live" ? "#10B981" : d.status === "failed" || d.status === "blocked" ? "#EF4444" : "#F59E0B" }} data-testid={`deployment-${d.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-md bg-slate-800/60 border border-slate-700 flex items-center justify-center">
                  <Globe className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <div className="font-display font-bold text-sm">{d.product_name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">by {d.agent_name} · v{d.active_version || "—"}</div>
                </div>
              </div>
              <StatusBadge status={d.status} testid={`deploy-status-${d.id}`} />
            </div>

            {(() => {
              const b = budgetFor(d.agent_id);
              if (!b) return null;
              const low = b.remaining < 50;
              return (
                <div className="mt-3 rounded-md border border-slate-800 bg-[#080B11] px-3 py-2" data-testid={`deploy-budget-${d.id}`}>
                  <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider">
                    <span className="flex items-center gap-1.5 text-slate-400"><Coins className="w-3 h-3" /> {b.name} · daily deploy budget</span>
                    <span className={low ? "text-amber-400" : "text-emerald-400"} data-testid={`deploy-budget-remaining-${d.id}`}>${b.remaining.toFixed(0)} left</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${b.pct}%`, backgroundColor: low ? "#F59E0B" : "#10B981" }} />
                  </div>
                  <div className="mt-1 text-[10px] font-mono text-slate-600">${b.spentToday.toFixed(0)} spent of ${b.cap.toFixed(0)} cap today</div>
                </div>
              );
            })()}

            {d.est_generation_cost != null && ["awaiting_approval", "backlog", "blocked"].includes(d.status) && (
              <div className="mt-2 flex items-center gap-1.5 rounded-md border border-slate-800 bg-[#080B11] px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-400" data-testid={`est-cost-${d.id}`}>
                <Coins className="w-3 h-3 text-cyan-400" />
                Est. generation cost <span className="text-cyan-300 ml-auto">~${Number(d.est_generation_cost).toFixed(2)}</span>
              </div>
            )}

            {d.live_url && (
              <a href={d.live_url} target="_blank" rel="noreferrer" data-testid={`deploy-url-${d.id}`}
                className="mt-3 flex items-center gap-2 rounded-md border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-xs font-mono text-emerald-400 hover:bg-emerald-500/10 transition-colors truncate">
                {d.ssl && <ShieldCheck className="w-3.5 h-3.5 shrink-0" />}
                <span className="truncate">{d.live_url}</span>
                <ExternalLink className="w-3.5 h-3.5 ml-auto shrink-0" />
              </a>
            )}

            {d.status === "awaiting_approval" && d.deploy_gate && (
              <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3" data-testid={`deploy-gate-${d.id}`}>
                <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-amber-400">
                  <ShieldCheck className="w-3.5 h-3.5" /> Production deploy · human approval required
                </div>
                <p className="mt-1.5 text-[11px] text-slate-400">A live-domain deploy is gated regardless of cost, so untrusted input can't auto-ship code. Approve to build & ship v{d.pending_version || 1}.</p>
                {isAdmin ? (
                  <div className="mt-3 flex gap-2">
                    <button data-testid={`deploy-approve-${d.id}`} onClick={() => approveDeploy(d, true)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 font-mono text-[10px] uppercase tracking-wider hover:bg-emerald-500/25 transition-colors">
                      <Check className="w-3 h-3" /> Approve deploy
                    </button>
                    <button data-testid={`deploy-reject-${d.id}`} onClick={() => approveDeploy(d, false)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md bg-red-500/15 border border-red-500/40 text-red-400 font-mono text-[10px] uppercase tracking-wider hover:bg-red-500/25 transition-colors">
                      <Ban className="w-3 h-3" /> Reject
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 flex items-center gap-1.5 text-[10px] font-mono text-slate-500"><Lock className="w-3 h-3" /> Admin approval required</div>
                )}
              </div>
            )}

            {d.review && (
              <div className={`mt-3 flex items-center gap-1.5 rounded-md border px-3 py-2 text-[11px] font-mono ${d.review.action === "approved" ? "border-emerald-500/25 bg-emerald-500/5 text-emerald-400" : "border-red-500/25 bg-red-500/5 text-red-400"}`} data-testid={`deploy-review-${d.id}`}>
                {d.review.action === "approved" ? <Check className="w-3.5 h-3.5 shrink-0" /> : <Ban className="w-3.5 h-3.5 shrink-0" />}
                <span className="truncate">
                  {d.review.action === "approved" ? "Approved" : "Rejected"} by {d.review.by}
                  {d.review.version ? ` · v${d.review.version}` : ""} · {new Date(d.review.at).toLocaleString()}
                  {d.review.note ? ` — ${d.review.note}` : ""}
                </span>
              </div>
            )}

            {d.status === "backlog" && (
              <div className="mt-3 rounded-md border border-sky-500/30 bg-sky-500/5 p-3" data-testid={`delivery-card-${d.id}`}>
                <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-sky-300">
                  <PackageCheck className="w-3.5 h-3.5" /> Awarded freelance project · ready to build
                </div>
                {d.description && <p className="mt-1.5 text-[11px] text-slate-400 line-clamp-3">{d.description}</p>}
                <button data-testid={`build-task-${d.id}`} onClick={() => buildTask(d)}
                  className="mt-3 flex items-center justify-center gap-1.5 py-2 px-4 rounded-md bg-sky-500/15 border border-sky-500/40 text-sky-300 font-mono text-[10px] uppercase tracking-wider hover:bg-sky-500/25 transition-colors">
                  <Rocket className="w-3 h-3" /> Build &amp; ship
                </button>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-1.5">
              {(d.status === "blocked" || d.status === "failed") && (
                <button data-testid={`retry-${d.id}`} onClick={() => retry(d)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-400 font-mono text-[10px] uppercase tracking-wider hover:bg-amber-500/20 transition-colors">
                  <RotateCcw className="w-3 h-3" /> Retry
                </button>
              )}
              <button data-testid={`redeploy-${d.id}`} onClick={() => redeploy(d)} disabled={d.status === "deploying" || d.status === "generating" || d.status === "awaiting_approval" || d.status === "backlog"}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 font-mono text-[10px] uppercase tracking-wider hover:bg-cyan-500/20 transition-colors disabled:opacity-40">
                <Rocket className="w-3 h-3" /> Redeploy (new version)
              </button>
              {d.source === "auto_delivery" && d.status === "live" && (
                <button data-testid={`revision-${d.id}`} onClick={() => requestRevision(d)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-violet-500/30 bg-violet-500/10 text-violet-400 font-mono text-[10px] uppercase tracking-wider hover:bg-violet-500/20 transition-colors">
                  <RotateCcw className="w-3 h-3" /> Request revision
                </button>
              )}
              <button data-testid={`logs-toggle-${d.id}`} onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 font-mono text-[10px] uppercase tracking-wider hover:bg-slate-800 transition-colors">
                <Terminal className="w-3 h-3" /> Logs & Versions
              </button>
            </div>

            {expanded === d.id && (
              <div className="mt-3 space-y-3">
                {(d.versions || []).length > 1 && (
                  <div>
                    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5"><History className="w-3 h-3" /> Version History</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(d.versions || []).map((v) => (
                        <button key={v.version} data-testid={`rollback-${d.id}-v${v.version}`} onClick={() => rollback(d, v.version)}
                          disabled={v.version === d.active_version}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded border font-mono text-[10px] transition-colors ${v.version === d.active_version ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-slate-700 text-slate-400 hover:bg-slate-800"}`}>
                          {v.version !== d.active_version && <RotateCcw className="w-3 h-3" />} v{v.version}{v.version === d.active_version ? " (live)" : ""}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {(d.spend_events || []).length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5"><Coins className="w-3 h-3" /> Money Trail</div>
                    <div className="rounded-md border border-slate-800 bg-[#050810] p-2 space-y-1" data-testid={`spend-trail-${d.id}`}>
                      {[...(d.spend_events || [])].reverse().map((ev, i) => {
                        const map = {
                          charged: { Icon: MinusCircle, cls: "text-amber-400", amt: `−$${Number(ev.amount).toFixed(0)}` },
                          refunded: { Icon: PlusCircle, cls: "text-emerald-400", amt: `+$${Number(ev.amount).toFixed(0)}` },
                          blocked: { Icon: AlertCircle, cls: "text-slate-500", amt: `$${Number(ev.amount).toFixed(0)} not charged` },
                        };
                        const m = map[ev.type] || map.blocked;
                        return (
                          <div key={i} className="flex items-center gap-2 font-mono text-[11px]" data-testid={`spend-event-${d.id}-${i}`}>
                            <m.Icon className={`w-3.5 h-3.5 shrink-0 ${m.cls}`} />
                            <span className={`font-semibold ${m.cls}`}>{m.amt}</span>
                            <span className="text-slate-500 truncate">{ev.note}{ev.version ? ` · v${ev.version}` : ""}</span>
                            <span className="text-slate-600 ml-auto shrink-0">{ev.at ? new Date(ev.at).toLocaleTimeString() : ""}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">Deployment Logs</div>
                  <div className="rounded-md border border-slate-800 bg-[#050810] p-3 max-h-40 overflow-y-auto space-y-1 font-mono text-[11px]" data-testid={`deploy-logs-${d.id}`}>
                    {(d.logs || []).map((l, i) => (
                      <div key={i} className={l.level === "critical" ? "text-red-400" : l.level === "warn" ? "text-amber-400" : "text-slate-400"}>
                        <span className="text-slate-600">{new Date(l.ts).toLocaleTimeString()} </span>{l.msg}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {showNew && <NewDeployment agents={agents} root={config.root_domain} onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function DomainSearch({ enabled }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const search = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/domains/search?q=${encodeURIComponent(q)}`);
      setResults(data.results);
    } catch (err) { toast.error(err.response?.data?.detail || "Search failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="hud-card accent-top rounded-lg p-5" style={{ "--accent-line": "#06B6D4" }}>
      <Overline>Domain Search (live registrar)</Overline>
      <form onSubmit={search} className="mt-3 flex gap-2">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="domain-search-input" value={q} onChange={(e) => setQ(e.target.value)} disabled={!enabled}
            placeholder="e.g. lunarcoffee" className="w-full pl-9 pr-3 py-2.5 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60 disabled:opacity-50" />
        </div>
        <button data-testid="domain-search-btn" type="submit" disabled={loading || !enabled}
          className="px-5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 transition-colors disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
        </button>
      </form>
      {results && (
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="domain-results">
          {results.map((r) => (
            <div key={r.domain} className={`flex items-center justify-between rounded-md border px-3 py-2 ${r.available ? "border-emerald-500/30 bg-emerald-500/5" : "border-slate-800 bg-[#080B11]"}`}>
              <span className="font-mono text-sm text-slate-200">{r.domain}</span>
              {r.available
                ? <span className="font-mono text-xs text-emerald-400">${r.price}/yr</span>
                : <span className="font-mono text-[10px] text-slate-500 uppercase">taken</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewDeployment({ agents, root, onClose, onDone }) {
  const [f, setF] = useState({ agent_id: agents[0]?.id || "", product_name: "", description: "", deploy_cost: 25 });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.agent_id) { toast.error("Create an active Builder agent first"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/deployments", { ...f, deploy_cost: Number(f.deploy_cost) });
      if (data.status === "generating" || data.status === "deploying") toast.success("Building & deploying — the site will appear live shortly");
      else if (data.status === "live") toast.success("Site deployed live with SSL");
      else if (data.status === "awaiting_approval") toast.warning("Production deploy needs admin approval — approve it on this page");
      else toast.error(`Deploy ${data.status}: ${(data.logs || []).slice(-1)[0]?.msg || ""}`);
      onDone();
    } catch (err) {
      toast.message("Still building — it will appear in the list shortly");
      onDone();
    }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md hud-card rounded-xl p-6 bg-[#0E131F]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-lg">New Deployment</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-3 text-xs text-slate-400 bg-cyan-500/5 border border-cyan-500/20 rounded-md p-3">
          A Builder agent will generate a landing page with AI and deploy it live to Vercel with automatic SSL{root ? <> on a branded <span className="text-cyan-400 font-mono">{"{agent}"}.{root}</span> subdomain</> : ""}. Deploy cost runs through the spend engine (policy-gated).
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400 mb-1 block">Builder Agent</label>
            <select data-testid="deploy-agent-select" value={f.agent_id} onChange={set("agent_id")} className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm">
              {agents.length === 0 && <option value="">No active Builder agents</option>}
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400 mb-1 block">Product / Site Name</label>
            <input data-testid="deploy-product-input" value={f.product_name} onChange={set("product_name")} required placeholder="Lunar Coffee" className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60" />
          </div>
          <div>
            <label className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400 mb-1 block">Brief / Description</label>
            <textarea data-testid="deploy-desc-input" value={f.description} onChange={set("description")} rows={2} placeholder="premium single-origin coffee subscription with monthly deliveries" className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60" />
          </div>
          <div>
            <label className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400 mb-1 block">Deploy Cost ($)</label>
            <input data-testid="deploy-cost-input" type="number" value={f.deploy_cost} onChange={set("deploy_cost")} className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60" />
          </div>
          <button type="submit" data-testid="deploy-submit-btn" disabled={loading} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Generate & Deploy <Rocket className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
